import { authorizedFetch, ErrorResponse } from './api-client';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface Clinic {
  id: string;
  cnpj: string;
  name: string;
  trade_name: string;
  phone: string;
  cellphone: string | null;
  postcode: string;
  address: string;
  additional_address: string;
  city_name: string;
  state_code: string;
  company_id: string;
  status: number;
  whapi_token: string | null;
  whapi_channel: string | null;
  twilio_whatsapp_from: string | null;
  telegram_bot_token: string | null;
  telegram_chat_id: string | null;
  whatsapp_provider: string;
  meta_phone_number_id: string | null;
  meta_waba_id: string | null;
  meta_bot_phone_number_id: string | null;
}

export interface ClinicSimple {
  id: string;
  name: string;
  trade_name: string;
}

export async function fetchClinicsByCompany(companyId: string): Promise<ClinicSimple[]> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/by-company/${companyId}`);
  if (!response.ok) throw new Error('Erro ao buscar clínicas da empresa');
  return response.json();
}

export async function fetchClinicById(id: string, complete = true): Promise<Clinic> {
  const params = new URLSearchParams({ complete: complete.toString() });
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${id}?${params.toString()}`);
  if (!response.ok) {
    throw new Error('Erro ao buscar clínica');
  }
  return response.json();
}

export async function fetchClinics(
  companyId?: string,
  includeInactive = false,
  complete = false,
  nameLike?: string,
  page = 1,
  pageSize = 20,
  clinicId?: string
): Promise<Clinic[]> {
  const params = new URLSearchParams({
    include_inactive: includeInactive.toString(),
    complete: complete.toString(),
    page: page.toString(),
    page_size: pageSize.toString(),
  });

  if (companyId) params.append('company_id', companyId);
  if (nameLike) params.append('name_like', nameLike);
  if (clinicId) params.append('clinic_id', clinicId);

  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/?${params.toString()}`);

  if (!response.ok) {
    throw new Error('Erro ao buscar clínicas');
  }

  const data = await response.json();
  return data.items ?? data;
}

export interface CreateClinicData {
  cnpj: string;
  name: string;
  trade_name: string;
  phone: string;
  cellphone?: string;
  postcode: string;
  address: string;
  additional_address?: string;
  city_name: string;
  state_code: string;
  company_id: string;
  whapi_token?: string;
  whapi_channel?: string;
  telegram_bot_token?: string;
  telegram_chat_id?: string;
  whatsapp_provider?: string;
  meta_phone_number_id?: string;
  meta_waba_id?: string;
  meta_bot_phone_number_id?: string;
  meta_access_token?: string;
}

export async function createClinic(data: CreateClinicData): Promise<Clinic> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao criar clínica');
  }

  return response.json();
}

export async function updateClinic(id: string, data: CreateClinicData): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao atualizar clínica');
  }
}

export async function activateClinic(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${id}/activate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao ativar clínica');
  }
}

export async function inactivateClinic(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${id}/inactivate`, {
    method: 'PATCH',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao inativar clínica');
  }
}

export async function deleteClinic(id: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error: ErrorResponse = await response.json();
    throw new Error(error.detail || 'Erro ao deletar clínica');
  }
}

// ── Clinic ↔ Dental Insurance Associations ──

export interface ClinicInsurance {
  id: string;
  name: string;
  status: number;
}

export async function fetchClinicInsurances(clinicId: string): Promise<ClinicInsurance[]> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${clinicId}/dental-insurances`);
  if (!response.ok) throw new Error('Erro ao buscar convênios da clínica');
  return response.json();
}

export async function associateClinicInsurance(clinicId: string, insuranceId: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${clinicId}/dental-insurances/${insuranceId}`, {
    method: 'POST',
  });
  if (!response.ok) throw new Error('Erro ao associar convênio');
}

export async function disassociateClinicInsurance(clinicId: string, insuranceId: string): Promise<void> {
  const response = await authorizedFetch(`${API_BASE_URL}/api/v1/clinics/${clinicId}/dental-insurances/${insuranceId}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Erro ao desassociar convênio');
}